import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type Theme = "light" | "dark";
export type ThemePreference = "auto" | Theme;

interface ThemeContextValue {
  // The theme actually applied right now - what every consumer (charts,
  // the .dark class, the quick-toggle button's icon) should read. In Auto
  // mode this tracks the clock; otherwise it's just themePreference.
  theme: Theme;
  // The user's stored choice: "auto" (follow the 10pm-6am night window),
  // or an explicit "light"/"dark" override. Kept separate from `theme` so
  // the Appearance menu can show which of the three options is selected,
  // not just the current light/dark state.
  themePreference: ThemePreference;
  setThemePreference: (pref: ThemePreference) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = "financify_theme_v1";

// "Night" for Auto mode's purposes: 10pm-6am local device time, exactly
// the window this was asked for - not sunset/sunrise or a geolocation
// lookup (no permission prompt for a cosmetic feature).
const NIGHT_START_HOUR = 22;
const NIGHT_END_HOUR = 6;

function autoTheme(date: Date = new Date()): Theme {
  const hour = date.getHours();
  return hour >= NIGHT_START_HOUR || hour < NIGHT_END_HOUR ? "dark" : "light";
}

// A returning visitor's explicit choice always wins; a first-time visitor
// always starts in light mode regardless of OS preference or time of day -
// Auto is an opt-in third choice, not the default, so this doesn't change
// behavior for anyone who hasn't asked for it. Reading localStorage
// synchronously (not in an effect) is what keeps a stored choice applied
// on the very first paint rather than flashing light-then-dark(-then-auto).
function readInitialPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "auto") return stored;
  } catch {
    // Private/incognito tabs can throw on localStorage access - fall
    // through to the light default below rather than crashing the app.
  }
  return "light";
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preference, setPreference] = useState<ThemePreference>(readInitialPreference);
  const [autoNow, setAutoNow] = useState<Theme>(() => autoTheme());

  const theme: Theme = preference === "auto" ? autoNow : preference;

  // Re-derives the effective theme as the clock crosses the 10pm/6am
  // boundary, without needing a page reload - a cheap poll interval
  // rather than a precisely-scheduled timeout, since it also naturally
  // absorbs a device's clock/timezone changing under a long-lived tab
  // (travel, DST). Re-checking on visibilitychange too covers a tab left
  // open overnight and only looked at again the next morning, where the
  // interval alone could otherwise sit paused in a backgrounded tab.
  useEffect(() => {
    if (preference !== "auto") return;
    const recompute = () => setAutoNow(autoTheme());
    recompute();
    const intervalId = window.setInterval(recompute, 60_000);
    document.addEventListener("visibilitychange", recompute);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", recompute);
    };
  }, [preference]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      // Best-effort only - worst case the choice doesn't persist across reloads.
    }
  }, [preference]);

  const setThemePreference = useCallback((pref: ThemePreference) => {
    setPreference(pref);
  }, []);

  // The quick-toggle affordances (the header's icon button, and the old
  // single "Switch to..." menu item) always set an explicit choice - the
  // opposite of whatever is showing right now - rather than doing nothing
  // useful while Auto is on. That's what "the user can always override"
  // means in practice: one click always flips light/dark immediately.
  // Getting back to Auto afterwards is a deliberate, separate choice (the
  // three-way Appearance menu), not something a quick toggle should
  // stumble back into on its own.
  const toggleTheme = useCallback(() => {
    setPreference((prev) => {
      const current: Theme = prev === "auto" ? autoTheme() : prev;
      return current === "dark" ? "light" : "dark";
    });
  }, []);

  const value = useMemo(
    () => ({ theme, themePreference: preference, setThemePreference, toggleTheme }),
    [theme, preference, setThemePreference, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}
